import { useRouter } from "next/router"
import { useCallback, useMemo, useState, useEffect } from "react"

import type { GeocodingResult } from "@bounce/google-place"
import { GooglePlace } from "@bounce/google-place"
import { useLocale } from "@bounce/i18n"

import { config } from "@/config"
import { InteractionEvents } from "@/libs/analytics/events"
import { trackSearchBar } from "@/utils/analytics"

type UseListViewSearchBarOptions = {
  from: "CityListView" | "POIListView"
  initialValue?: string
}

export const useListViewSearchBar = ({ from, initialValue = "" }: UseListViewSearchBarOptions) => {
  const { language } = useLocale()
  const router = useRouter()
  const placeService = useMemo(() => GooglePlace(config.googleMaps.key, language), [language])

  const [searchTerm, _setSearchTerm] = useState<GeocodingResult | undefined>()
  const [searchBarContent, setSearchBarContent] = useState<string>(initialValue)

  useEffect(() => {
    setSearchBarContent(initialValue)
  }, [initialValue])

  const resolveGeolocation = useCallback(
    (searchQuery: string) => {
      return placeService.getGeocodingFromAddress(searchQuery)
    },
    [placeService],
  )

  const setSearchTerm = useCallback(
    async (newSearchTerm: string | GeocodingResult | undefined) => {
      if (typeof newSearchTerm === "string") {
        const geocoding = await resolveGeolocation(newSearchTerm)
        _setSearchTerm(geocoding)
        if (geocoding) {
          trackSearchBar({
            event: InteractionEvents.LuggageStorageSearchBarLocationSuggestionPressed,
            props: {
              from,
              pageName: router.route,
              pagePath: router.asPath,
              searchQuery: geocoding.query,
              searchQueryType: geocoding.scope,
              searchedCountry: geocoding.country,
              searchedCity: geocoding.city,
              searchedType: geocoding.scope,
              searchedSubtype: geocoding.type,
            },
          })
        }
      } else {
        _setSearchTerm(newSearchTerm)
        setSearchBarContent(newSearchTerm?.query || "")
      }
    },
    [resolveGeolocation, router, from],
  )

  return {
    searchTerm,
    searchBarContent,
    setSearchTerm,
    setSearchBarContent,
  }
}
